// src/pages/Admin/ApplicationsDashboard.js
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AdminGate from "../../components/AdminGate";

/* -------------------- helpers -------------------- */
function formatDate(ts) {
  try {
    if (!ts) return "—";
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
    if (typeof ts === "number") {
      const t = ts > 1e12 ? ts : ts * 1000;
      return new Date(t).toLocaleString();
    }
    const d = new Date(ts);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
}
function clsx(...xs) {
  return xs.filter(Boolean).join(" ");
}
function downloadCSV(filename, rows) {
  if (!rows?.length) return;
  const seen = new Set(Object.keys(rows[0] || {}));
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => seen.add(k)));
  const headers = Array.from(seen);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const StatusPill = ({ status }) => {
  const s = String(status || "pending").toLowerCase();
  const map = {
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
    pending: "bg-amber-100 text-amber-700",
  };
  const label = s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "Pending";
  return <span className={clsx("inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold", map[s] || map.pending)}>{label}</span>;
};

/* ===================== main page ===================== */
export default function ApplicationsDashboard() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qText, setQText] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [sortKey, setSortKey] = useState("submittedAt:desc");
  const [error, setError] = useState("");

  // pagination
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // view / edit modals
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    const qRef = query(collection(db, "applications"), orderBy("submittedAt", "desc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = snap.docs.map((d) => {
          const data = d.data() || {};
          let s = (data.status || "pending").toString().toLowerCase();
          if (["submitted", "new", "applied"].includes(s)) s = "pending";
          if (!["pending", "approved", "rejected"].includes(s)) s = "pending";
          return {
            id: d.id,
            status: s,
            files: data.files || [],
            submittedAt: data.submittedAt || null,
            ...data,
          };
        });
        setApps(arr);
        setLoading(false);
      },
      (err) => {
        console.error("[applications] snapshot error", err);
        setError("Failed to load applications.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const states = useMemo(() => Array.from(new Set(apps.map((a) => a.state).filter(Boolean))).sort(), [apps]);
  const districts = useMemo(
    () => Array.from(new Set(apps.filter((a) => !stateFilter || a.state === stateFilter).map((a) => a.district).filter(Boolean))).sort(),
    [apps, stateFilter]
  );

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    let rows = apps.filter((a) => {
      if (stateFilter && a.state !== stateFilter) return false;
      if (districtFilter && a.district !== districtFilter) return false;
      if (!t) return true;
      const hay = `${a.societyName || ""} ${a.applicantName || ""} ${a.district || ""} ${a.village || ""} ${a.regNo || ""} ${a.email || ""}`.toLowerCase();
      return hay.includes(t);
    });

    const [k, dir = "asc"] = sortKey.split(":");
    rows.sort((a, b) => {
      const m = dir === "asc" ? 1 : -1;
      if (k === "submittedAt") {
        const aa = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : new Date(a.submittedAt || 0).getTime();
        const bb = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : new Date(b.submittedAt || 0).getTime();
        return (aa - bb) * m;
      }
      if (k === "name") {
        return String(a.societyName || a.applicantName || "").localeCompare(String(b.societyName || b.applicantName || "")) * m;
      }
      if (k === "status") {
        return String(a.status).localeCompare(String(b.status)) * m;
      }
      return 0;
    });

    return rows;
  }, [apps, qText, stateFilter, districtFilter, sortKey]);

  // pagination derived
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  /* ---------------- actions ---------------- */
  async function changeStatus(appId, status) {
    try {
      const ref = doc(db, "applications", appId);
      const payload = { status };
      if (status === "approved") {
        payload.approvedAt = serverTimestamp();
      } else if (status === "rejected") {
        payload.rejectedAt = serverTimestamp();
      }
      await updateDoc(ref, payload);
      // audit trail
      await setDoc(doc(collection(db, "applications", appId, "audits")), {
        action: `status:${status}`,
        at: serverTimestamp(),
      });
    } catch (e) {
      console.error("[changeStatus] error", e?.code, e?.message, e);
      alert(`Failed to update status: ${e?.code || ""} ${e?.message || ""}`);
    }
  }

  async function removeApplication(appId) {
    if (!confirm("Delete this application permanently?")) return;
    try {
      await deleteDoc(doc(db, "applications", appId));
    } catch (e) {
      console.error("[delete] error", e);
      alert("Failed to delete application.");
    }
  }

  function exportCSV() {
    const rows = filtered.map((r) => ({
      AppID: r.appId || r.id,
      SocietyName: r.societyName || "",
      RegNo: r.regNo || "",
      Applicant: r.applicantName || "",
      Email: r.email || "",
      Phone: r.phone || "",
      State: r.state || "",
      District: r.district || "",
      Taluka: r.taluka || "",
      Village: r.village || "",
      Pincode: r.pincode || "",
      Capacity_MT: r.godownCapacity || "",
      Status: r.status || "",
      SubmittedAt: formatDate(r.submittedAt),
      Files: (r.files || []).map((f) => f.url || f.name || "").join(" | "),
    }));
    downloadCSV(`applications_${new Date().toISOString().slice(0, 10)}.csv`, rows);
  }

  /* ---------------- render ---------------- */
  return (
    <AdminGate>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Logout Bar */}
<div className="flex justify-end mb-4">
  <button
    onClick={() => {
      import("../../services/adminAuth").then(({ adminLogout }) => {
        adminLogout();
        window.location.href = "/admin-login";
      });
    }}
    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
  >
    Logout
  </button>
</div>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-indigo-600 to-teal-500">
                Scheme Applications
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Manage applications with realtime updates, filters and quick actions.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={exportCSV} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                Export CSV
              </button>
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                Refresh
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="flex items-center gap-3 flex-1">
                <input
                  value={qText}
                  onChange={(e) => {
                    setQText(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search society / applicant / village / reg no / email…"
                  className="flex-1 min-w-[220px] border border-slate-300 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setDistrictFilter("");
                    setPage(1);
                  }}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All States</option>
                  {states.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <select
                  value={districtFilter}
                  onChange={(e) => {
                    setDistrictFilter(e.target.value);
                    setPage(1);
                  }}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Districts</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="submittedAt:desc">Submitted ↓</option>
                  <option value="submittedAt:asc">Submitted ↑</option>
                  <option value="name:asc">Name A→Z</option>
                  <option value="name:desc">Name Z→A</option>
                  <option value="status:asc">Status A→Z</option>
                  <option value="status:desc">Status Z→A</option>
                </select>
              </div>

              <div className="text-sm text-slate-600">
                Showing <strong>{filtered.length}</strong> result(s)
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3">{error}</div>
          )}

          {/* Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 text-left">Society / Applicant</th>
                    <th className="p-3 text-left">Location</th>
                    <th className="p-3 text-left">Capacity</th>
                    <th className="p-3 text-left">Submitted</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left w-56">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        Loading applications…
                      </td>
                    </tr>
                  ) : pageData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        No applications found.
                      </td>
                    </tr>
                  ) : (
                    pageData.map((r) => (
                      <tr key={r.id} className="border-t hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-semibold text-slate-900">{r.societyName || "—"}</div>
                          <div className="text-xs text-slate-500">
                            {r.applicantName || "—"} • {r.email || r.submittedByEmail || "—"}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-slate-900">{r.state || "—"} / {r.district || "—"}</div>
                          <div className="text-xs text-slate-500">{r.taluka || "—"} • {r.village || "—"} • {r.pincode || "—"}</div>
                        </td>
                        <td className="p-3">{r.godownCapacity ? `${r.godownCapacity} MT` : "—"}</td>
                        <td className="p-3 text-xs text-slate-600">{formatDate(r.submittedAt)}</td>
                        <td className="p-3">
                          <StatusPill status={r.status} />
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-2">
                            {r.status === "pending" && (
                              <>
                                <button
                                  onClick={() => changeStatus(r.id, "approved")}
                                  className="px-3 py-1 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => changeStatus(r.id, "rejected")}
                                  className="px-3 py-1 bg-rose-600 text-white rounded-lg text-xs hover:bg-rose-700"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                setViewing(r);
                                setEditing(null);
                              }}
                              className="px-3 py-1 bg-slate-800 text-white rounded-lg text-xs hover:bg-black/90"
                            >
                              View
                            </button>
                            <button
                              onClick={() => {
                                setEditing(r);
                                setEditData(r);
                                setViewing(null);
                              }}
                              className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => removeApplication(r.id)}
                              className="px-3 py-1 bg-slate-200 text-slate-800 rounded-lg text-xs hover:bg-slate-300"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="flex items-center justify-between p-4 border-t bg-slate-50">
              <div className="text-xs text-slate-600">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  First
                </button>
                <button
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                <div className="px-3 py-1 text-sm text-slate-700">{page} / {totalPages}</div>
                <button
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
                <button
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                >
                  Last
                </button>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="ml-2 border border-slate-300 rounded-lg px-2 py-1 text-sm"
                >
                  <option value={5}>5 / page</option>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                </select>
              </div>
            </div>
          </div>

          {/* View Drawer / Modal */}
          {viewing && (
            <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-4">
              <div className="bg-white w-full max-w-3xl rounded-2xl p-6 shadow-2xl relative">
                <button
                  className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700"
                  onClick={() => setViewing(null)}
                >
                  ×
                </button>
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  Application — {viewing.appId || viewing.id}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Society</div>
                    <div className="font-semibold text-slate-900">{viewing.societyName || "—"}</div>
                    <div className="text-xs text-slate-600">{viewing.regNo || "—"}</div>

                    <div className="mt-4 text-xs text-slate-500 mb-1">Applicant</div>
                    <div className="font-medium text-slate-900">{viewing.applicantName || "—"}</div>
                    <div className="text-xs text-slate-600">{viewing.email || viewing.submittedByEmail || "—"}</div>

                    <div className="mt-4 text-xs text-slate-500 mb-1">Location</div>
                    <div className="text-slate-900">{viewing.state || "—"} / {viewing.district || "—"}</div>
                    <div className="text-xs text-slate-600">{viewing.taluka || "—"} • {viewing.village || "—"} • {viewing.pincode || "—"}</div>

                    <div className="mt-4 text-xs text-slate-500 mb-1">Godown Capacity</div>
                    <div className="font-semibold text-slate-900">{viewing.godownCapacity ? `${viewing.godownCapacity} MT` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Address</div>
                    <div className="text-sm text-slate-900">{viewing.address || "—"}</div>
                    <div className="mt-4 text-xs text-slate-500 mb-1">Files</div>
                    <div className="space-y-2 mt-2">
                      {viewing.files?.length ? (
                        viewing.files.map((f, i) => (
                          <div key={i} className="flex items-center justify-between gap-3 border border-slate-200 p-2 rounded-lg bg-slate-50">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">{f.name || f.url}</div>
                              {f.size ? <div className="text-xs text-slate-500">{Math.round((f.size || 0) / 1024)} KB</div> : null}
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={f.url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                              >
                                Open
                              </a>
                              <a
                                href={f.url}
                                download
                                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                              >
                                Download
                              </a>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-slate-500">No files uploaded</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300"
                    onClick={() => setViewing(null)}
                  >
                    Close
                  </button>
                  {viewing.status !== "approved" && (
                    <button
                      className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => changeStatus(viewing.id, "approved")}
                    >
                      Approve
                    </button>
                  )}
                  {viewing.status !== "rejected" && (
                    <button
                      className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
                      onClick={() => changeStatus(viewing.id, "rejected")}
                    >
                      Reject
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Edit Modal */}
          {editing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-3xl p-6 relative shadow-2xl">
                <button
                  className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700"
                  onClick={() => setEditing(null)}
                >
                  ×
                </button>
                <h3 className="text-lg font-bold text-slate-900 mb-4">Edit Application — {editing.appId || editing.id}</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField label="Society Name" value={editData.societyName} onChange={(v) => setEditData((x) => ({ ...x, societyName: v }))} />
                  <TextField label="Registration No." value={editData.regNo} onChange={(v) => setEditData((x) => ({ ...x, regNo: v }))} />
                  <TextField label="Applicant Name" value={editData.applicantName} onChange={(v) => setEditData((x) => ({ ...x, applicantName: v }))} />
                  <TextField label="Email" value={editData.email} onChange={(v) => setEditData((x) => ({ ...x, email: v }))} />
                  <TextArea label="Address" value={editData.address} onChange={(v) => setEditData((x) => ({ ...x, address: v }))} rows={3} />
                  <TextField label="District" value={editData.district} onChange={(v) => setEditData((x) => ({ ...x, district: v }))} />
                  <TextField label="Taluka" value={editData.taluka} onChange={(v) => setEditData((x) => ({ ...x, taluka: v }))} />
                  <TextField label="Village" value={editData.village} onChange={(v) => setEditData((x) => ({ ...x, village: v }))} />
                  <TextField label="Pincode" value={editData.pincode} onChange={(v) => setEditData((x) => ({ ...x, pincode: v }))} />
                  <TextField label="Godown Capacity (MT)" value={editData.godownCapacity} onChange={(v) => setEditData((x) => ({ ...x, godownCapacity: v }))} />
                </div>

                <div className="mt-6 text-right">
                  <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300 mr-2" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    onClick={async () => {
                      try {
                        await updateDoc(doc(db, "applications", editing.id), editData);
                        setEditing(null);
                      } catch (e) {
                        console.error("[edit save] error", e);
                        alert("Failed to save edits.");
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminGate>
  );
}

/* -------------------- small inputs -------------------- */
function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div className="md:col-span-2">
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
}
